import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RefreshCw, Sparkles, Trash2, FileText, Zap } from "lucide-react";
import { format } from "date-fns";

interface SEOKeyword {
  id: string;
  keyword: string;
  intent: string;
  difficulty: string;
  suggested_blog_title: string | null;
  status: string;
  notes: string | null;
  generated_at: string;
  created_at: string;
}

const intentColors: Record<string, string> = {
  informational: "bg-blue-100 text-blue-700",
  transactional: "bg-green-100 text-green-700",
  local: "bg-purple-100 text-purple-700",
};

const difficultyColors: Record<string, string> = {
  easy: "bg-green-100 text-green-700",
  medium: "bg-yellow-100 text-yellow-700",
  hard: "bg-red-100 text-red-700",
};

const AdminSEOKeywords = () => {
  const queryClient = useQueryClient();
  const [generating, setGenerating] = useState(false);
  const [generatingBlog, setGeneratingBlog] = useState<string | null>(null);
  const [intentFilter, setIntentFilter] = useState("all");
  const [difficultyFilter, setDifficultyFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: keywords = [], isLoading } = useQuery({
    queryKey: ["seo-keywords"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("seo_keywords" as any)
        .select("*")
        .order("generated_at", { ascending: false });
      if (error) throw error;
      return data as unknown as SEOKeyword[];
    },
  });

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-seo-keywords");
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Failed to generate");
      toast.success(`Generated ${data.count} keywords`);
      queryClient.invalidateQueries({ queryKey: ["seo-keywords"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to generate keywords");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateBlog = async (kw: SEOKeyword, autoPublish: boolean) => {
    const actionId = `${kw.id}-${autoPublish ? "publish" : "draft"}`;
    setGeneratingBlog(actionId);
    try {
      const { data, error } = await supabase.functions.invoke("generate-blog-post", {
        body: {
          keyword: kw.keyword,
          suggested_blog_title: kw.suggested_blog_title || kw.keyword,
          auto_publish: autoPublish,
          keyword_id: kw.id,
        },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Failed to generate blog post");
      toast.success(
        autoPublish
          ? `Blog post "${data.post?.title}" published!`
          : `Blog post draft "${data.post?.title}" created!`
      );
      queryClient.invalidateQueries({ queryKey: ["seo-keywords"] });
      queryClient.invalidateQueries({ queryKey: ["admin-blog"] });
      queryClient.invalidateQueries({ queryKey: ["blog-posts"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to generate blog post");
    } finally {
      setGeneratingBlog(null);
    }
  };

  const handleUpdateStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from("seo_keywords" as any)
      .update({ status } as any)
      .eq("id", id);
    if (error) {
      toast.error("Failed to update status");
    } else {
      queryClient.invalidateQueries({ queryKey: ["seo-keywords"] });
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase
      .from("seo_keywords" as any)
      .delete()
      .eq("id", id);
    if (error) {
      toast.error("Failed to delete");
    } else {
      toast.success("Keyword deleted");
      queryClient.invalidateQueries({ queryKey: ["seo-keywords"] });
    }
  };

  const filtered = keywords.filter((k) => {
    if (intentFilter !== "all" && k.intent !== intentFilter) return false;
    if (difficultyFilter !== "all" && k.difficulty !== difficultyFilter) return false;
    if (statusFilter !== "all" && k.status !== statusFilter) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">SEO Keywords</h1>
          <p className="text-sm text-muted-foreground font-body">
            AI-generated keyword suggestions for blog content ({keywords.length} total)
          </p>
        </div>
        <Button onClick={handleGenerate} disabled={generating}>
          {generating ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4 mr-2" />
          )}
          {generating ? "Generating…" : "Generate Keywords"}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={intentFilter} onValueChange={setIntentFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Intent" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Intents</SelectItem>
            <SelectItem value="informational">Informational</SelectItem>
            <SelectItem value="transactional">Transactional</SelectItem>
            <SelectItem value="local">Local</SelectItem>
          </SelectContent>
        </Select>
        <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Difficulty" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Difficulty</SelectItem>
            <SelectItem value="easy">Easy</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="hard">Hard</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="in-progress">In Progress</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Keyword</TableHead>
              <TableHead>Intent</TableHead>
              <TableHead>Difficulty</TableHead>
              <TableHead className="min-w-[250px]">Suggested Blog Title</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Generated</TableHead>
              <TableHead className="min-w-[200px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Loading…</TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                  No keywords found. Click "Generate Keywords" to get started.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((kw) => {
                const isDraftLoading = generatingBlog === `${kw.id}-draft`;
                const isPublishLoading = generatingBlog === `${kw.id}-publish`;
                const isAnyLoading = generatingBlog !== null;

                return (
                  <TableRow key={kw.id}>
                    <TableCell className="font-medium">{kw.keyword}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={intentColors[kw.intent] || ""}>
                        {kw.intent}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={difficultyColors[kw.difficulty] || ""}>
                        {kw.difficulty}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{kw.suggested_blog_title}</TableCell>
                    <TableCell>
                      <Select value={kw.status} onValueChange={(v) => handleUpdateStatus(kw.id, v)}>
                        <SelectTrigger className="w-32 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="in-progress">In Progress</SelectItem>
                          <SelectItem value="published">Published</SelectItem>
                          <SelectItem value="dismissed">Dismissed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {format(new Date(kw.generated_at), "d MMM yyyy")}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs gap-1"
                          disabled={isAnyLoading}
                          onClick={() => handleGenerateBlog(kw, false)}
                        >
                          {isDraftLoading ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <FileText className="w-3 h-3" />
                          )}
                          {isDraftLoading ? "Creating…" : "Draft"}
                        </Button>
                        <Button
                          size="sm"
                          className="h-8 text-xs gap-1"
                          disabled={isAnyLoading}
                          onClick={() => handleGenerateBlog(kw, true)}
                        >
                          {isPublishLoading ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <Zap className="w-3 h-3" />
                          )}
                          {isPublishLoading ? "Publishing…" : "Auto Publish"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(kw.id)}
                          disabled={isAnyLoading}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminSEOKeywords;
